import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Mail, Clock, MapPin, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LangContext";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";

const COPY = {
  fr: {
    eyebrow: "Service client",
    lead:
      "Une question sur nos produits de bien-être — le Coussin Massant Chauffant ou la Sphère Duo Massage Froid & Chaud ? Un doute sur votre commande, un retour, une facture, une garantie ? Notre équipe française répond à chaque email, personnellement, sous 24 heures ouvrées.",
    lead2:
      "myFLOVEA est édité par EM GROUP SASU, entreprise française basée à Paris. Nous prenons le temps de vous répondre — parce qu'un service client de qualité fait partie intégrante d'un produit bien-être premium.",
    channelsTitle: "Vous pouvez nous joindre par",
    channels: [
      { icon: Mail, label: "Email", value: "contact@myflovea.com" },
      { icon: Clock, label: "Délai de réponse", value: "24h ouvrées maximum" },
      { icon: MapPin, label: "Adresse postale", value: "60 Rue François Ier, 75008 Paris" },
      { icon: ShieldCheck, label: "Support conforme", value: "RGPD · Médiation consommation" },
    ],
    formTitle: "Écrivez-nous directement",
    formNote:
      "Vos données sont utilisées uniquement pour traiter votre demande, ne sont jamais revendues, et sont conservées 3 ans maximum. Vous pouvez à tout moment y accéder ou les supprimer depuis notre page dédiée RGPD.",
  },
  en: {
    eyebrow: "Customer service",
    lead:
      "A question about our wellness products — the Heated Massage Cushion or the Duo Cold & Hot Massage Sphere? Doubts about an order, return, invoice or warranty? Our French team replies to every email, personally, within 24 business hours.",
    lead2:
      "myFLOVEA is published by EM GROUP SASU, a French company based in Paris. We take the time to answer — because premium wellness is nothing without premium service.",
    channelsTitle: "How to reach us",
    channels: [
      { icon: Mail, label: "Email", value: "contact@myflovea.com" },
      { icon: Clock, label: "Reply time", value: "24 business hours max" },
      { icon: MapPin, label: "Postal address", value: "60 Rue François Ier, 75008 Paris" },
      { icon: ShieldCheck, label: "Compliant support", value: "GDPR · Consumer mediation" },
    ],
    formTitle: "Send us a direct message",
    formNote:
      "Your data is used only to process your request, never sold, and kept for a maximum of 3 years. You can view or delete it any time via our GDPR page.",
  },
};

const Contact = () => {
  const { t, lang } = useLang();
  const copy = COPY[lang];
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitContact(form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success(t.legal.contactSuccess);
    } catch (_err) {
      toast.error(lang === "fr" ? "Erreur lors de l'envoi" : "Submission error");
    } finally {
      setSubmitting(false);
    }
  };

  // JSON-LD ContactPage + Organization schema
  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: lang === "fr" ? "Contact myFLOVEA" : "Contact myFLOVEA",
    url: "https://myflovea.com/contact",
    mainEntity: {
      "@type": "Organization",
      name: "myFLOVEA",
      legalName: "EM GROUP SASU",
      email: "contact@myflovea.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "60 Rue François Ier",
        postalCode: "75008",
        addressLocality: "Paris",
        addressCountry: "FR",
      },
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer service",
        email: "contact@myflovea.com",
        availableLanguage: ["French", "English"],
      },
    },
  };

  return (
    <section
      data-testid="contact-page"
      className="max-w-3xl mx-auto px-6 sm:px-10 py-20 md:py-28"
    >
      <Seo
        lang={lang}
        path="/contact"
        title={lang === "fr" ? "Contact — Service client 24h" : "Contact — 24h customer service"}
        description={
          lang === "fr"
            ? "Contactez le service client myFLOVEA. Réponse garantie sous 24h ouvrées, équipe francophone basée à Paris. Email, adresse postale et support conforme RGPD."
            : "Contact the myFLOVEA customer service. Guaranteed reply within 24 business hours, French-speaking team based in Paris. Email, postal address, GDPR-compliant support."
        }
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
      </Helmet>

      <div className="eyebrow mb-6">{copy.eyebrow}</div>
      <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight">
        {t.legal.contactTitle}
      </h1>

      <p className="mt-8 text-[17px] leading-relaxed text-[#4a4a4a] max-w-2xl">
        {copy.lead}
      </p>
      <p className="mt-4 text-[15px] leading-relaxed text-[#4a4a4a] max-w-2xl">
        {copy.lead2}
      </p>

      {/* Channels block */}
      <div className="mt-12 border-t border-b border-[#dcd7ce] py-8">
        <div className="eyebrow mb-6 text-[#8B6343]">{copy.channelsTitle}</div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {copy.channels.map((c, i) => {
            const Icon = c.icon;
            return (
              <li
                key={i}
                data-testid={`contact-channel-${i}`}
                className="flex items-start gap-4"
              >
                <div className="w-10 h-10 rounded-full bg-[#F5EFE6] border border-[#dcd7ce] flex items-center justify-center shrink-0">
                  <Icon size={16} strokeWidth={1.5} className="text-[#8B6343]" />
                </div>
                <div>
                  <div className="eyebrow text-[10px] text-[#8a8378] mb-1">{c.label}</div>
                  <div className="text-[15px] text-[#1A1A1A] leading-relaxed">{c.value}</div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <h2 className="mt-14 font-serif-display text-2xl md:text-3xl text-[#1A1A1A]">
        {copy.formTitle}
      </h2>

      <form onSubmit={onSubmit} className="mt-8 space-y-8" data-testid="contact-form">
        <label className="block">
          <span className="eyebrow mb-2 block">{t.checkout.fields.firstName}</span>
          <input
            type="text"
            name="name"
            required
            value={form.name}
            onChange={onChange}
            data-testid="contact-name"
            className="w-full bg-transparent border-b border-[#dcd7ce] focus:border-[#1A1A1A] outline-none py-3 text-[15px]"
          />
        </label>
        <label className="block">
          <span className="eyebrow mb-2 block">{t.checkout.fields.email}</span>
          <input
            type="email"
            name="email"
            required
            value={form.email}
            onChange={onChange}
            data-testid="contact-email"
            className="w-full bg-transparent border-b border-[#dcd7ce] focus:border-[#1A1A1A] outline-none py-3 text-[15px]"
          />
        </label>
        <label className="block">
          <span className="eyebrow mb-2 block">{lang === "fr" ? "Sujet" : "Subject"}</span>
          <input
            type="text"
            name="subject"
            required
            value={form.subject}
            onChange={onChange}
            data-testid="contact-subject"
            className="w-full bg-transparent border-b border-[#dcd7ce] focus:border-[#1A1A1A] outline-none py-3 text-[15px]"
          />
        </label>
        <label className="block">
          <span className="eyebrow mb-2 block">Message</span>
          <textarea
            name="message"
            rows={5}
            required
            value={form.message}
            onChange={onChange}
            data-testid="contact-message"
            className="w-full bg-transparent border border-[#dcd7ce] focus:border-[#1A1A1A] outline-none p-4 text-[15px] resize-none rounded-sm"
          />
        </label>
        <button
          type="submit"
          disabled={submitting}
          data-testid="contact-submit"
          className="bg-[#1A1A1A] text-[#F5F0EB] px-10 py-4 text-xs tracking-[0.22em] uppercase hover:bg-[#333] transition-colors disabled:opacity-60 rounded-sm"
        >
          {submitting ? "…" : t.legal.contactSubmit}
        </button>
        {sent && (
          <div className="text-sm text-[#4a4a4a]" data-testid="contact-success">
            {t.legal.contactSuccess}
          </div>
        )}
        <p className="text-xs text-[#8a8378] leading-relaxed max-w-xl">{copy.formNote}</p>
      </form>
    </section>
  );
};

export default Contact;
